/**
 * A stable colour per speaker (ARCHITECTURE.md §65).
 *
 * ============================ WHY A PALETTE ============================
 * "Random name colours" is the familiar Twitch behaviour, and implemented
 * literally — a random hue per user — it eventually produces a colour that
 * cannot be read on Connaro's surfaces. Twitch's own defaults include
 * `#0000FF`, which sits at roughly 2.4:1 against `--c-bg` and is exactly the
 * failure an operator already reported for the Twitch brand mark.
 *
 * So the colour is CHOSEN FROM a fixed palette rather than generated. Every
 * entry is checked against the darkest surface in `name-colors.test.ts`, which
 * is the same discipline `platform-icon-contrast.test.ts` applies to the brand
 * marks, and for the same reason: contrast is measurable, so it should be
 * measured rather than eyeballed.
 *
 * Deterministic, so one person is one colour for as long as they keep the
 * name. A colour that changed between sessions would be worse than none — the
 * whole value is recognising a regular without reading.
 * =======================================================================
 */

/**
 * Twelve hues, all at or above 4.5:1 on `--c-bg` (#0c0e12).
 *
 * Twelve rather than more: the point is telling speakers apart at a glance in
 * a fast feed, and past a dozen the eye stops distinguishing them anyway,
 * while the odds of two adjacent rows colliding barely improve.
 */
export const NAME_COLORS: readonly string[] = [
  '#ff8a80', // coral
  '#ffb74d', // amber
  '#ffd54f', // gold
  '#aed581', // light green
  '#4db6ac', // teal
  '#4fc3f7', // sky
  '#7f9cf5', // periwinkle
  '#b388ff', // violet
  '#f48fb1', // pink
  '#a1887f', // taupe
  '#90a4ae', // slate
  '#80cbc4', // aqua
];

/**
 * FNV-1a, chosen because it is stable across machines and runs.
 *
 * `String.prototype.hashCode` does not exist, and a sum of char codes collides
 * on anagrams — "bob" and "obb" would share a colour, which is precisely the
 * case a chat feed produces with names built from the same few characters.
 */
function hash(value: string): number {
  let h = 0x811c9dc5;

  for (let i = 0; i < value.length; i += 1) {
    h ^= value.charCodeAt(i);
    // >>> 0 keeps it an unsigned 32-bit value; Math.imul avoids the precision
    // loss a plain multiply would hit past 2^53.
    h = Math.imul(h, 0x01000193) >>> 0;
  }

  return h;
}

/**
 * The colour for a speaker, or undefined when the feature is off.
 *
 * Lower-cased first: Twitch treats `Bob` and `bob` as one account, and two
 * colours for one person would defeat the purpose.
 */
export function nameColor(name: string, enabled: boolean): string | undefined {
  if (!enabled || name === '') return undefined;

  return NAME_COLORS[hash(name.toLowerCase()) % NAME_COLORS.length];
}
